import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import TierSelector from './TierSelector';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import {
    Zap, MapPin, Trophy, Navigation, Bike, BarChart3,
    Coins, Star, ChevronRight, Gift, AlertTriangle, X
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Dashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [recentRides, setRecentRides] = useState([]);
    const [challenges, setChallenges] = useState([]);
    const [loading, setLoading] = useState(true);
    const [maintAlerts, setMaintAlerts] = useState([]);
    const [subLoaded, setSubLoaded] = useState(false);
    const [needsTier, setNeedsTier] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const subSessionId = params.get('sub_session_id');
        const clean = () => window.history.replaceState({}, '', '/dashboard');

        const run = async () => {
            if (subSessionId) {
                try {
                    const r = await fetch(`${API}/subscription/confirm/${subSessionId}`, { credentials: 'include' });
                    const d = await r.json();
                    if (r.ok && d.selected) {
                        toast.success('Standard plan activated — welcome to Boltorium!');
                        setNeedsTier(false);
                        setSubLoaded(true);
                        clean();
                        return;
                    }
                } catch (e) { console.warn('Confirm sub:', e.message); }
            }
            if (params.get('sub_cancel')) clean();
            try {
                const r = await fetch(`${API}/subscription/me`, { credentials: 'include' });
                const d = await r.json();
                setNeedsTier(r.ok ? !d.selected : false);
            } catch (e) {
                console.warn('Sub me:', e.message);
                setNeedsTier(false);
            } finally {
                setSubLoaded(true);
            }
        };
        run();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [statsRes, ridesRes, challengesRes, alertsRes] = await Promise.all([
                    fetch(`${API}/rider/stats`, { credentials: 'include' }),
                    fetch(`${API}/rides`, { credentials: 'include' }),
                    fetch(`${API}/challenges/mine`, { credentials: 'include' }),
                    fetch(`${API}/maintenance/alerts/active`, { credentials: 'include' }).catch(() => null),
                ]);

                if (statsRes.ok) setStats(await statsRes.json());
                if (ridesRes.ok) setRecentRides((await ridesRes.json()).slice(0, 3));
                if (challengesRes.ok) setChallenges((await challengesRes.json()).slice(0, 3));
                if (alertsRes?.ok) setMaintAlerts(await alertsRes.json());
            } catch (error) {
                console.error('Dashboard fetch error:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const xpProgress = stats ? (stats.xp / stats.xpToNextLevel) * 100 : 0;

    if (!subLoaded) return <div className="min-h-screen bg-background" data-testid="tier-loading" />;
    if (needsTier) return <TierSelector onDone={() => setNeedsTier(false)} />;

    return (
        <Layout>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
                {/* Welcome Header */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8"
                >
                    <p className="label-text text-primary text-glow">WELCOME BACK</p>
                    <h1 className="text-2xl sm:text-4xl font-black mt-1 font-display text-foreground tracking-wide">
                        {user?.first_name || user?.name?.split(' ')[0] || 'Rider'}
                    </h1>
                    <p className="text-muted-foreground mt-1" style={{ fontFamily: 'var(--font-body)' }}>
                        Systems online. Ready to ride.
                    </p>
                </motion.div>

                {/* Maintenance Alerts */}
                {maintAlerts.length > 0 && (
                    <div className="space-y-2 mb-6">
                        {maintAlerts.map(alert => (
                            <motion.div key={alert.alert_id} initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}
                                className={`rounded-xl p-3 flex items-center gap-3 border ${
                                    alert.priority === 'critical' ? 'bg-destructive/10 border-destructive/30' :
                                    alert.priority === 'high' ? 'bg-orange-500/10 border-orange-500/30' :
                                    'bg-yellow-500/10 border-yellow-500/30'
                                }`} data-testid={`maint-alert-${alert.alert_id}`}>
                                <AlertTriangle className={`h-4 w-4 shrink-0 ${
                                    alert.priority === 'critical' ? 'text-destructive' : 'text-yellow-500'
                                }`} />
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-foreground">{alert.title}</p>
                                    {alert.description && <p className="text-xs text-muted-foreground truncate">{alert.description}</p>}
                                    {alert.scheduled_at && <p className="text-[10px] text-muted-foreground mt-0.5">Scheduled: {new Date(alert.scheduled_at).toLocaleString()}</p>}
                                </div>
                                <button onClick={() => setMaintAlerts(prev => prev.filter(a => a.alert_id !== alert.alert_id))}
                                    className="text-muted-foreground hover:text-foreground p-1">
                                    <X className="h-3 w-3" />
                                </button>
                            </motion.div>
                        ))}
                    </div>
                )}

                {/* Bento Grid */}
                <div className="bento-grid">
                    {/* Boltz Balance - Large */}
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="md:col-span-2"
                    >
                        <Card className="glass-card h-full overflow-hidden border-boltz-gold/20 bg-boltz-gold/5 box-glow-gold" data-testid="boltz-balance-card">
                            <CardContent className="p-6 flex flex-col justify-between h-full min-h-[200px]">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <span className="label-text text-boltz-gold">BOLTZ BALANCE</span>
                                        {loading ? (
                                            <Skeleton className="h-12 w-32 mt-2 bg-white/10" />
                                        ) : (
                                            <p className="stat-number text-boltz-gold text-glow-gold mt-2" data-testid="boltz-balance-value">
                                                {stats?.boltzBalance?.toFixed(2) || user?.boltz_balance?.toFixed(2) || '0.00'}
                                            </p>
                                        )}
                                    </div>
                                    <div className="w-12 h-12 bg-boltz-gold/20 rounded-full flex items-center justify-center shadow-[0_0_15px_rgba(255,180,0,0.2)]">
                                        <img src="/boltz-coin-new.png" alt="Boltz" className="w-8 h-8 object-contain" />
                                    </div>
                                </div>
                                <div className="flex gap-3 mt-4">
                                    <Button
                                        onClick={() => navigate('/ride')}
                                        className="flex-1 btn-primary"
                                        data-testid="start-ride-btn"
                                    >
                                        <Navigation className="mr-2 h-4 w-4" />
                                        Start Ride
                                    </Button>
                                    <Button
                                        onClick={() => navigate('/wallet')}
                                        variant="outline"
                                        className="flex-1 border-white/10 text-foreground hover:bg-white/5 rounded-full"
                                        data-testid="view-wallet-btn"
                                    >
                                        View Wallet
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>

                    {/* Level & XP */}
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.15 }}
                    >
                        <Card className="glass-card h-full box-glow" data-testid="level-card">
                            <CardContent className="p-6">
                                <span className="label-text">VOLT TIER</span>
                                {loading ? (
                                    <Skeleton className="h-12 w-16 mt-2 bg-white/10" />
                                ) : (
                                    <>
                                        <p className="text-2xl sm:text-3xl font-black font-display mt-2" style={{ color: stats?.voltTierColor || '#1E90FF' }} data-testid="volt-tier-name">
                                            {stats?.voltTier || 'Spark'}
                                        </p>
                                        <p className="text-xs text-muted-foreground font-mono mt-1">
                                            Lv.{stats?.level || user?.level || 1} | {stats?.voltMultiplier || 1.0}x Boltz
                                        </p>
                                    </>
                                )}
                                <div className="mt-4">
                                    <div className="flex justify-between text-xs text-muted-foreground mb-1 font-mono">
                                        <span>{stats?.xp || 0} XP</span>
                                        <span>{stats?.xpToNextLevel || 100} XP</span>
                                    </div>
                                    <Progress value={xpProgress} className="h-2" />
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>

                    {/* Rank */}
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                    >
                        <Card
                            className="glass-card h-full cursor-pointer box-glow-pink"
                            onClick={() => navigate('/leaderboard')}
                            data-testid="rank-card"
                        >
                            <CardContent className="p-6">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <span className="label-text">RANK</span>
                                        {loading ? (
                                            <Skeleton className="h-12 w-12 mt-2 bg-white/10" />
                                        ) : (
                                            <p className="stat-number text-hot-pink text-glow-pink mt-2">#{stats?.rank || '-'}</p>
                                        )}
                                    </div>
                                    <div className="w-10 h-10 bg-hot-pink/15 rounded-full flex items-center justify-center">
                                        <Trophy size={20} className="text-hot-pink" />
                                    </div>
                                </div>
                                <p className="text-sm text-muted-foreground mt-3 flex items-center" style={{ fontFamily: 'var(--font-body)' }}>
                                    View Leaderboard <ChevronRight size={14} className="ml-1" />
                                </p>
                            </CardContent>
                        </Card>
                    </motion.div>

                    {/* Total Distance */}
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.25 }}
                    >
                        <Card className="glass-card h-full box-glow" data-testid="distance-card">
                            <CardContent className="p-6">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <span className="label-text">TOTAL DISTANCE</span>
                                        {loading ? (
                                            <Skeleton className="h-10 w-24 mt-2 bg-white/10" />
                                        ) : (
                                            <p className="text-2xl sm:text-3xl font-black font-mono mt-2 text-foreground">
                                                {stats?.totalDistance?.toFixed(1) || '0.0'}
                                                <span className="text-lg text-muted-foreground ml-1">km</span>
                                            </p>
                                        )}
                                    </div>
                                    <div className="w-10 h-10 bg-cyan-glow/15 rounded-full flex items-center justify-center">
                                        <BarChart3 size={20} className="text-cyan-glow" />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>

                    {/* Total Rides */}
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                    >
                        <Card className="glass-card h-full box-glow" data-testid="rides-count-card">
                            <CardContent className="p-6">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <span className="label-text">TOTAL RIDES</span>
                                        {loading ? (
                                            <Skeleton className="h-10 w-16 mt-2 bg-white/10" />
                                        ) : (
                                            <p className="text-2xl sm:text-3xl font-black font-mono mt-2 text-neon-green">
                                                {stats?.totalRides || 0}
                                            </p>
                                        )}
                                    </div>
                                    <div className="w-10 h-10 bg-neon-green/15 rounded-full flex items-center justify-center">
                                        <Bike size={20} className="text-neon-green" />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>

                    {/* Vehicle */}
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.35 }}
                    >
                        <Card
                            className="glass-card h-full cursor-pointer box-glow"
                            onClick={() => navigate('/profile')}
                            data-testid="vehicle-card"
                        >
                            <CardContent className="p-6">
                                <span className="label-text">VEHICLE</span>
                                <p className="text-lg font-bold mt-2 capitalize text-foreground font-display">
                                    {user?.vehicle_type?.replace('_', ' ') || 'eBike'}
                                </p>
                                <p className="text-sm text-muted-foreground mt-1 flex items-center" style={{ fontFamily: 'var(--font-body)' }}>
                                    Change Vehicle <ChevronRight size={14} className="ml-1" />
                                </p>
                            </CardContent>
                        </Card>
                    </motion.div>
                </div>

                {/* Quick Actions */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4"
                >
                    {[
                        { icon: Navigation, label: 'Start Ride', path: '/ride', color: 'text-primary' },
                        { icon: Gift, label: 'Find Airdrops', path: '/airdrops', color: 'text-neon-gold' },
                        { icon: Trophy, label: 'Challenges', path: '/challenges', color: 'text-hot-pink' },
                        { icon: Star, label: 'Shop', path: '/shop', color: 'text-cyan-glow' },
                    ].map((action) => (
                        <Button
                            key={action.label}
                            variant="outline"
                            className="h-auto py-4 flex flex-col items-center gap-2 rounded-xl border-white/10 hover:bg-white/5 hover:border-primary/30 transition-all duration-300"
                            onClick={() => navigate(action.path)}
                            data-testid={`quick-${action.label.toLowerCase().replace(/\s/g, '-')}`}
                        >
                            <action.icon size={24} className={action.color} />
                            <span className="text-xs font-medium text-foreground tracking-wider uppercase" style={{ fontFamily: 'var(--font-display)' }}>
                                {action.label}
                            </span>
                        </Button>
                    ))}
                </motion.div>

                {/* Recent Activity */}
                {recentRides.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.45 }}
                        className="mt-8"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-bold font-display text-foreground">Recent Rides</h2>
                            <Button variant="ghost" size="sm" onClick={() => navigate('/ride')} className="text-primary hover:text-primary/80 font-mono text-sm">
                                View All <ChevronRight size={16} className="ml-1" />
                            </Button>
                        </div>
                        <div className="space-y-3">
                            {recentRides.map((ride) => (
                                <Card key={ride.ride_id} className="glass-card">
                                    <CardContent className="p-4 flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                                                <Bike size={20} className="text-primary" />
                                            </div>
                                            <div>
                                                <p className="font-medium text-foreground">{ride.distance?.toFixed(2)} km</p>
                                                <p className="text-sm text-muted-foreground font-mono">
                                                    {new Date(ride.start_time).toLocaleDateString()}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-mono font-bold text-neon-gold text-glow-gold">+{ride.boltz_earned?.toFixed(2)}</p>
                                            <p className="text-xs text-muted-foreground font-display">Boltz</p>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </motion.div>
                )}
            </div>
        </Layout>
    );
}
